<template>
  <div class="big-screen">
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>
    <!-- 顶部标题栏 -->
    <header class="screen-header">
      <button class="back-btn" @click="$router.push('/main')" >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>
      <h1 class="header-title">大数据可视化统计后台</h1>
      <div class="header-clock">
        <span class="clock-time">{{ currentTime }}</span>
        <span class="clock-date">{{ currentDate }}</span>
      </div>
    </header>

    <!-- 主体内容 3x2 网格 -->
    <div class="screen-body">
      <!-- 第1行 第1列：地域用群画像 -->
      <div class="panel">
        <div class="panel-title">
          <span>地域用群画像</span>
        </div>
        <div class="panel-content">
          <div ref="regionChart" class="chart-container"></div>
        </div>
      </div>

      <!-- 第1行 第2列：今日广告收入 + 用户统计 + 六宫格 -->
      <div class="panel panel-center-top">
        <div class="panel-title">
          <span>今日实时数据概况</span>
        </div>
        <div class="panel-content panel-center-content">
          <div class="center-top">

            <div class="online-section">
              <div class="online-numbers">
                <div class="number-circle" v-for="(ch, idx) in adRevenueDigits" :key="idx + '-' + ch">{{ ch }}</div>
              </div>
            </div>
    
          </div>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-value">{{ userStats.teamLeaderCount }}</div>
              <div class="stat-label"><span class="stat-dot"></span>团长人数</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" :style="statValueStyle(agentTotalEarnedFormatted)">{{ agentTotalEarnedFormatted }}</div>
              <div class="stat-label"><span class="stat-dot"></span>团长总收益</div>
            </div>
            <div class="stat-card highlight">
              <div class="stat-value">{{ totalUsersFormatted }}</div>
              <div class="stat-label"><span class="stat-dot"></span>用户总数</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ todayNewUsers }}</div>
              <div class="stat-label"><span class="stat-dot"></span>今日新增用户</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ yesterdayNewUsers }}</div>
              <div class="stat-label"><span class="stat-dot"></span>昨日认证用户</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ riskRuleCount }}</div>
              <div class="stat-label"><span class="stat-dot"></span>风控规则数</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ riskListCounts.deviceBlackCount }}</div>
              <div class="stat-label"><span class="stat-dot"></span>设备黑名单数</div>
            </div>
            <div class="stat-card highlight">
              <div class="stat-value">{{ onlineCountTotal }}</div>
              <div class="stat-label"><span class="stat-dot"></span>实时在线人数</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ riskListCounts.packageBlackCount }}</div>
              <div class="stat-label"><span class="stat-dot"></span>包名黑名单数</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ riskListCounts.packageWhiteCount }}</div>
              <div class="stat-label"><span class="stat-dot"></span>包名白名单数</div>
            </div>
          </div>
         
        </div>
      </div>

      <!-- 第1行 第3列：用户累计余额排行榜 -->
      <div class="panel">
        <div class="panel-title">
          <span>用户累计余额排行榜</span>
        </div>
        <div class="panel-content">
          <div class="earned-list">
            <div class="earned-item" v-for="(item, idx) in earnedRankList" :key="idx">
              <div class="earned-rank" :class="{top3: idx < 3}">{{ idx + 1 }}</div>
              <img
                class="earned-avatar"
                :src="item.avatarUrl || defaultAvatar"
                :key="item.avatarUrl"
              />
              <div class="earned-name">{{ item.nickname }}</div>
              <div class="earned-bar">
                <div class="earned-progress" :style="{width: item._percent + '%'}"></div>
              </div>
              <div class="earned-value">{{ item._displayAmount }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 第2行 第1列：提现概览 -->
      <div class="panel">
        <div class="panel-title">
          <span>资金概况</span>
        </div>
        <div class="panel-content withdraw-panel">
          <div class="withdraw-grid">
            <!-- 左上：累计提现 -->
            <div class="wd-card">
              <div class="wd-label">累计提现</div>
              <div class="wd-value wd-value-highlight">¥{{ withdrawOverview.totalAmount | formatWDMoney }}</div>
              <div class="wd-wave">
                <div class="wd-wave-bar" v-for="(h, i) in waveHeights1" :key="'bal-'+i" :style="{height: (40 + h * 110) + '%'}"></div>
              </div>
            </div>
            <!-- 右上：通过率环形图 -->
            <div class="wd-card wd-card-chart">
              <div class="wd-label">今日审核通过率</div>
              <div class="wd-donut-wrap">
                <div ref="withdrawChart" class="wd-donut"></div>
                <div class="wd-donut-legend">
                  <span><i style="background:#00d4ff;"></i>已通过 {{ withdrawOverview.approvedCount }}</span>
                  <span><i style="background:#1a4580;"></i>已拒绝 {{ withdrawOverview.rejectedCount }}</span>
                </div>
              </div>
            </div>
            <!-- 左下：今日提现金额 -->
            <div class="wd-card">
              <div class="wd-label">今日提现金额</div>
              <div class="wd-value wd-value-highlight">¥{{ withdrawOverview.todayAmount | formatWDMoney }}</div>
              <div class="wd-wave">
                <div class="wd-wave-bar" v-for="(h, i) in waveHeights2" :key="'in-'+i" :style="{height: (40 + h * 110) + '%'}"></div>
              </div>
            </div>
            <!-- 右下：待审核 -->
            <div class="wd-card">
              <div class="wd-label">今日待审核数量</div>
              <div class="wd-value wd-pending">{{ withdrawOverview.pendingCount }}</div>
              <div class="wd-wave">
                <div class="wd-wave-bar" v-for="(h, i) in waveHeights3" :key="'out-'+i" :style="{height: (40 + h * 110) + '%'}"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 第2行 第2列：今日广告类型记录概述 -->
      <div class="panel">
        <div class="panel-title">
          <span>今日广告类型记录概述</span>
        </div>
        <div class="panel-content ad-overview" :class="{ 'ad-overview-empty': !adTypeRevenue || adTypeRevenue.length === 0 }">
          <template v-if="adTypeRevenue && adTypeRevenue.length > 0">
            <div class="ad-chart-area">
              <div ref="pieChart" class="pie-chart"></div>
            </div>
            <div class="ad-legend">
              <div class="legend-item" v-for="item in adTypeRevenue" :key="item.adType" :style="{ background: getAdTypeBgColor(item.adType) }">
                <span class="dot" :style="{background: getAdTypeColor(item.adType)}"></span>
                <span class="name">{{ getAdTypeName(item.adType) }}</span>
              </div>
            </div>
          </template>
          <div v-else class="ad-empty-hint">
            <span class="ad-empty-text">今日暂无广告记录</span>
          </div>
        </div>
      </div>

      <!-- 第2行 第3列：近6个月新增用户概况 -->
      <div class="panel">
        <div class="panel-title">
          <span>近6个月新增用户概况</span>
        </div>
        <div class="panel-content">
          <div ref="monthChart" class="chart-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { get } from '@/utils/http'

export default {
  name: 'DataDashboardV3',
  data() {
    return {
      onlineCount: ['0', '0', '0', '0', '0', '0', '0'],
      onlineTimer: null,
      dataRefreshTimer: null,
      particleTimer: null,
      particleCtx: null,
      pieColors: ['rgba(2, 109, 178, 1)', 'rgba(0, 132, 220, 1)', 'rgba(92, 100, 255, 1)', 'rgba(45, 225, 253, 1)', 'rgba(0, 86, 255, 1)', '#40c4ff', '#0091ea', '#00acc1', '#0277bd', '#80deea'],
      earnedRankList: [],
      monthlyNewUsers: [],
      adTypeRevenue: [],
      userStats: { totalUsers: 0, teamLeaderCount: 0, totalAgentEarned: 0 },
      todayNewUsers: 0,
      yesterdayNewUsers: 0,
      topCities: [],
      regionPage: 0,
      regionCycleTimer: null,
      todayTotalEcpm: 0,
      riskRuleCount: 0,
      riskListCounts: { deviceBlackCount: 0, packageBlackCount: 0, packageWhiteCount: 0 },
      withdrawOverview: { todayAmount: 0, totalAmount: 0, pendingCount: 0, approvedCount: 0, rejectedCount: 0, approvalRate: 0 },
      waveHeights1: [0.35, 0.55, 0.7, 0.9, 0.75, 0.6, 0.85, 0.95, 0.65, 0.5],
      waveHeights2: [0.8, 0.5, 0.65, 0.4, 0.95, 0.7, 0.55, 0.85, 0.6, 0.9],
      waveHeights3: [0.6, 0.85, 0.5, 0.7, 0.4, 0.95, 0.75, 0.55, 0.9, 0.65],
      defaultAvatar: require('@/assets/logo.png'),
      currentTime: '',
      currentDate: '',
      clockTimer: null,
      regionChart: null,
      gaugeChart: null,
      pieChart: null,
      monthChart: null,
      withdrawChart: null
    }
  },
  computed: {
    adTypeTotal() {
      return this.adTypeRevenue.reduce((sum, i) => sum + (i.ecpm || 0), 0)
    },
    agentTotalEarnedFormatted() {
      const v = (this.userStats.totalAgentEarned || 0) / 1000
      return this.formatAmount(v)
    },
    todayTotalEcpmFormatted() {
      const v = this.todayTotalEcpm / 1000
      return this.formatAmount(v)
    },
    totalUsersFormatted() {
      const v = this.userStats.totalUsers
      if (v >= 10000) {
        return (v / 10000).toFixed(2) + '万'
      }
      return String(v)
    },
    chartFontBase() {
      return Math.max(11, Math.round(window.innerWidth / 150))
    },
    chartFontSmall() {
      return Math.max(9, Math.round(window.innerWidth / 190))
    },
    chartFontTitle() {
      return Math.max(14, Math.round(window.innerWidth / 110))
    },
    // 广告收入拆分为字符数组，用于数字翻牌器显示（始终保持7位，尾部补零）
    adRevenueDigits() {
      const v = this.todayTotalEcpm / 1000
      const isWan = v >= 10000
      const numStr = isWan ? (v / 10000).toFixed(2) : v.toFixed(2)
      // 50.21 → "50.2100" | 500.21 → "500.210"
      return numStr.padEnd(7, '0').split('')
    },
    // 实时在线总人数转为字符串，供统计卡片显示
    onlineCountTotal() {
      const num = parseInt(this.onlineCount.join(''), 10) || 0
      if (num >= 10000) {
        return (num / 10000).toFixed(2) + '万'
      }
      return String(num)
    }
  },
  filters: {
    formatWDMoney(val) {
      if (val == null) return '0'
      const v = val / 1000
      if (v >= 10000) return (v / 10000).toFixed(2) + '万'
      return v.toFixed(2)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initRegionChart()
      this.initGaugeChart()
      this.initPieChart()
      this.initMonthChart()
      this.initWithdrawChart()
    })
    this.startClock()
    this.fetchTopEarned()
    this.fetchMonthlyNewUsers()
    this.fetchTodayAdTypeRevenue()
    this.fetchOnlineUsers()
    this.fetchUserStatistics()
    this.fetchTodayNewUsers()
    this.fetchYesterdayNewUsers()
    this.fetchTopCities()
    this.fetchTodayTotalEcpm()
    this.fetchRiskRuleCount()
    this.fetchRiskListCounts()
    this.fetchWithdrawOverview()
    this.initParticles()
    this.startAutoRefresh()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
    if (this.clockTimer) clearInterval(this.clockTimer)
    if (this.dataRefreshTimer) clearInterval(this.dataRefreshTimer)
    if (this.particleTimer) cancelAnimationFrame(this.particleTimer)
    if (this.regionCycleTimer) clearInterval(this.regionCycleTimer)
  },
  methods: {
    startAutoRefresh() {
      this.fetchAllData()
      this.dataRefreshTimer = setInterval(() => this.fetchAllData(), 10000)
    },
    fetchAllData() {
      this.fetchOnlineUsers()
      this.fetchTopEarned()
      this.fetchMonthlyNewUsers()
      this.fetchTodayAdTypeRevenue()
      this.fetchUserStatistics()
      this.fetchTodayNewUsers()
      this.fetchYesterdayNewUsers()
      this.fetchTopCities()
      this.fetchTodayTotalEcpm()
      this.fetchRiskRuleCount()
      this.fetchRiskListCounts()
      this.fetchWithdrawOverview()
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      this.particleCtx = ctx
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      const particles = []
      const count = 60
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 2.5 + 0.8,
          dx: (Math.random() - 0.5) * 1.2,
          dy: (Math.random() - 0.5) * 1.2,
          alpha: Math.random() * 0.6 + 0.2,
          alphaDelta: (Math.random() - 0.5) * 0.015,
          color: Math.random() > 0.5 ? 'rgba(0,180,255,' : 'rgba(100,180,255,'
        })
      }
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        particles.forEach(p => {
          // 更新位置
          p.x += p.dx
          p.y += p.dy
          if (p.x < -10) p.x = canvas.width + 10
          if (p.x > canvas.width + 10) p.x = -10
          if (p.y < -10) p.y = canvas.height + 10
          if (p.y > canvas.height + 10) p.y = -10
          // 透明度呼吸
          p.alpha += p.alphaDelta
          if (p.alpha > 0.7 || p.alpha < 0.15) p.alphaDelta *= -1
          // 绘制粒子
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
          ctx.fillStyle = p.color + p.alpha + ')'
          ctx.fill()
          // 光晕
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.r * 2.5, 0, Math.PI * 2)
          ctx.fillStyle = p.color + (p.alpha * 0.3) + ')'
          ctx.fill()
        })
        // 连线
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x
            const dy = particles[i].y - particles[j].y
            const dist = Math.sqrt(dx * dx + dy * dy)
            if (dist < 120) {
              ctx.beginPath()
              ctx.moveTo(particles[i].x, particles[i].y)
              ctx.lineTo(particles[j].x, particles[j].y)
              ctx.strokeStyle = 'rgba(0,160,255,' + (0.08 * (1 - dist / 120)) + ')'
              ctx.lineWidth = 0.6
              ctx.stroke()
            }
          }
        }
        this.particleTimer = requestAnimationFrame(animate)
      }
      animate()
    },
    startClock() {
      const tick = () => {
        const now = new Date()
        const hh = String(now.getHours()).padStart(2, '0')
        const mm = String(now.getMinutes()).padStart(2, '0')
        const ss = String(now.getSeconds()).padStart(2, '0')
        this.currentTime = `${hh}:${mm}:${ss}`
        const y = now.getFullYear()
        const m = String(now.getMonth() + 1).padStart(2, '0')
        const d = String(now.getDate()).padStart(2, '0')
        const week = ['日', '一', '二', '三', '四', '五', '六'][now.getDay()]
        this.currentDate = `${y}-${m}-${d} 星期${week}`
      }
      tick()
      this.clockTimer = setInterval(tick, 1000)
    },
    async fetchTopEarned() {
      try {
        const res = await get('/admin/dashboard/topEarned')
        const list = res.data || []
        const scaledList = list.map(item => ({
          ...item,
          _scaled: (item.totalEarned || 0) / 1000
        }))
        const maxVal = scaledList.length > 0 ? Math.max(...scaledList.map(i => i._scaled)) : 1
        this.earnedRankList = scaledList.map(item => ({
          ...item,
          _percent: maxVal > 0 ? (item._scaled / maxVal * 100) : 0,
          _displayAmount: this.formatAmount(item._scaled)
        }))
      } catch (e) {
        console.error('获取排行榜失败:', e)
        this.earnedRankList = []
      }
    },
    formatAmount(val) {
      if (val >= 10000) {
        return (val / 10000).toFixed(2) + '万元'
      }
      return val.toFixed(2) + '元'
    },
    statValueStyle(text) {
      const len = (text || '').length
      if (len <= 6) return {}
      const scale = Math.max(0.55, 1 - (len - 6) * 0.085)
      return { fontSize: `calc(1.4vw * ${scale})` }
    },
    async fetchMonthlyNewUsers() {
      try {
        const res = await get('/admin/dashboard/monthlyNewUsers')
        this.monthlyNewUsers = res.data || []
      } catch (e) {
        console.error('获取新增用户数据失败:', e)
        this.monthlyNewUsers = []
      }
      this.updateMonthChart()
    },
    updateMonthChart() {
      if (!this.monthChart) return
      const data = this.monthlyNewUsers
      if (!data || data.length === 0) return
      const _x = data.map(i => {
        const parts = i.month.split('-')
        return parseInt(parts[1], 10) + '月'
      })
      const seriesData = data.map(i => i.count)
      const xData = _x
      const maxVal = Math.ceil(Math.max(...seriesData) * 1.2) || 10
      this.monthChart.setOption({
        xAxis: { data: xData },
        yAxis: { max: maxVal },
        series: [{
          data: seriesData,
          label: {
            formatter: function (params) {
              return seriesData[params.dataIndex]
            }
          }
        }, {
          data: seriesData
        }]
      })
    },
    getAdTypeName(type) {
      const map = {
        splash: '开屏',
        reward: '激励',
        banner: '横幅',
        interstitial: '插屏',
        native_feed: '信息流'
      }
      return map[type] || type
    },
    getAdTypeColor(type) {
      const map = {
        splash: 'linear-gradient(180deg, rgba(2, 109, 178, 1) 0%, rgba(18, 254, 224, 1) 100%)',
        reward: 'linear-gradient(180deg, rgba(0, 132, 220, 1) 0%, rgba(64, 196, 255, 1) 100%)',
        banner: 'linear-gradient(180deg, rgba(92, 100, 255, 1) 0%, rgba(105, 136, 248, 1) 100%)',
        interstitial: 'linear-gradient(180deg, rgba(45, 225, 253, 1) 0%, rgba(14, 95, 255, 1) 100%)',
        native_feed: 'linear-gradient(133.22deg, rgba(0, 86, 255, 1) 0%, rgba(61, 127, 255, 1) 100%)'
      }
      return map[type] || '#4488ff'
    },
    getAdTypeBgColor(type) {
      const map = {
        splash: 'rgba(2, 109, 178, 0.18)',
        reward: 'rgba(0, 132, 220, 0.18)',
        banner: 'rgba(92, 100, 255, 0.18)',
        interstitial: 'rgba(45, 225, 253, 0.18)',
        native_feed: 'rgba(0, 86, 255, 0.18)'
      }
      return map[type] || 'rgba(0, 120, 200, 0.12)'
    },
    getEChartsPieColor(type) {
      const map = {
        splash: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(2, 109, 178, 1)' },
          { offset: 1, color: 'rgba(18, 254, 224, 1)' }
        ]),
        reward: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(0, 132, 220, 1)' },
          { offset: 1, color: 'rgba(64, 196, 255, 1)' }
        ]),
        banner: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(92, 100, 255, 1)' },
          { offset: 1, color: 'rgba(105, 136, 248, 1)' }
        ]),
        interstitial: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(45, 225, 253, 1)' },
          { offset: 1, color: 'rgba(14, 95, 255, 1)' }
        ]),
        native_feed: new echarts.graphic.LinearGradient(1, 0, 0, 1, [
          { offset: 0, color: 'rgba(0, 86, 255, 1)' },
          { offset: 1, color: 'rgba(61, 127, 255, 1)' }
        ])
      }
      return map[type] || '#4488ff'
    },
    async fetchTodayAdTypeRevenue() {
      try {
        const res = await get('/admin/dashboard/todayAdTypeRevenue')
        this.adTypeRevenue = res.data || []
      } catch (e) {
        console.error('获取广告类型数据失败:', e)
        this.adTypeRevenue = []
      }
      await this.$nextTick()
      if (!this.pieChart && this.$refs.pieChart) {
        this.initPieChart()
      } else {
        this.updatePieChart()
      }
    },
    async fetchOnlineUsers() {
      try {
        const res = await get('/admin/dashboard/onlineUsers')
        const count = res.data || 0
        const str = String(count).padStart(7, '0')
        this.onlineCount = str.split('')
      } catch (e) {
        console.error('获取实时在线人数失败:', e)
      }
    },
    async fetchUserStatistics() {
      try {
        const res = await get('/admin/dashboard/users/statistics')
        this.userStats = res.data || { totalUsers: 0, teamLeaderCount: 0, totalAgentEarned: 0 }
      } catch (e) {
        console.error('获取用户统计失败:', e)
      }
    },
    async fetchTodayNewUsers() {
      try {
        const res = await get('/admin/dashboard/todayNewUsers')
        this.todayNewUsers = res.data || 0
      } catch (e) {
        console.error('获取今日新增用户失败:', e)
      }
    },
    async fetchYesterdayNewUsers() {
      try {
        const res = await get('/admin/dashboard/yesterdayNewUsers')
        this.yesterdayNewUsers = res.data || 0
      } catch (e) {
        console.error('获取昨日认证用户失败:', e)
      }
    },
    async fetchTodayTotalEcpm() {
      try {
        const res = await get('/admin/dashboard/todayTotalEcpm')
        this.todayTotalEcpm = res.data || 0
      } catch (e) {
        console.error('获取今日广告总eCPM失败:', e)
      }
    },
    async fetchRiskRuleCount() {
      try {
        const res = await get('/admin/dashboard/riskRules/count')
        this.riskRuleCount = res.data || 0
      } catch (e) {
        console.error('获取风控规则数失败:', e)
      }
    },
    async fetchRiskListCounts() {
      try {
        const res = await get('/admin/dashboard/riskListCounts')
        const raw = res.data || {}
        this.riskListCounts = {
          deviceBlackCount: raw.deviceBlacklistCount || 0,
          packageBlackCount: raw.pkgBlacklistCount || 0,
          packageWhiteCount: raw.pkgWhitelistCount || 0
        }
      } catch (e) {
        console.error('获取风控名单条数失败:', e)
      }
    },
    updatePieChart() {
      if (!this.pieChart) return
      const data = this.adTypeRevenue
      if (!data || data.length === 0) return
      const pieData = data.map(item => ({
        value: (item.ecpm || 0) / 1000,
        name: this.getAdTypeName(item.adType),
        itemStyle: { color: this.getEChartsPieColor(item.adType) }
      }))
      const haloOuter = pieData.map(d => ({
        ...d,
        itemStyle: { color: d.itemStyle.color, opacity: 0.08 }
      }))
      const haloInner = pieData.map(d => ({
        ...d,
        itemStyle: { color: d.itemStyle.color, opacity: 0.25 }
      }))
      this.pieChart.setOption({
        series: [
          {}, {}, {}, // 索引0-2：刻度环不变
          { data: haloOuter }, // 索引3：外层漫射光晕
          { data: haloInner }, // 索引4：内层光晕环
          { // 索引5：主数据环
            data: pieData,
            label: {
              show: true,
              position: 'outside',
              formatter: function (params) {
                return `{name|${params.name}}\n{value|¥${params.value.toFixed(2)}}\n{percent|${params.percent}%}`
              },
              rich: {
                name: { color: '#c8d8f0', fontSize: 11, lineHeight: 16, align: 'center' },
                value: { color: '#00e5ff', fontSize: 12, lineHeight: 18, fontWeight: 'bold', align: 'center' },
                percent: { color: '#ffaa00', fontSize: 10, lineHeight: 14, align: 'center' }
              }
            },
            labelLine: { show: true, length: 12, length2: 16, lineStyle: { color: 'rgba(0, 200, 255, 0.4)', width: 1.5 } }
          }
        ]
      })
    },
    initRegionChart() {
      if (!this.$refs.regionChart) return
      this.regionChart = echarts.init(this.$refs.regionChart)
      this.updateRegionChart()
    },
    updateRegionChart() {
      if (!this.regionChart) return
      const allData = (this.topCities && this.topCities.length > 0)
        ? this.topCities.map(i => ({ name: i.city, value: i.userCount }))
        : [
            { name: '北京', value: 0 },
            { name: '上海', value: 0 },
            { name: '广州', value: 0 },
            { name: '深圳', value: 0 },
            { name: '成都', value: 0 },
            { name: '杭州', value: 0 },
            { name: '西安', value: 0 },
            { name: '长沙', value: 0 },
            { name: '厦门', value: 0 },
            { name: '武汉', value: 0 },
            { name: '郑州', value: 0 },
            { name: '天津', value: 0 },
            { name: '石家庄', value: 0 },
            { name: '哈尔滨', value: 0 }
          ]
      // 滑动窗口：始终展示8条，逐条上下轮播，确保全部城市都能轮播到
      const showCount = 8
      const total = allData.length
      const start = this.regionPage % total
      let data = allData.slice(start, start + showCount)
      if (data.length < showCount) {
        data = data.concat(allData.slice(0, showCount - data.length))
      }
      this.regionChart.setOption({
        animationDurationUpdate: 1000,
        animationEasing: 'linear',
        grid: { left: '3%', right: '15%', top: '5%', bottom: '5%', containLabel: true },
        xAxis: { type: 'value', show: false },
        yAxis: {
          type: 'category',
          inverse: true,
          data: data.map(i => i.name),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#a0c0e0', fontSize: this.chartFontSmall }
        },
        series: [
          {
            type: 'bar',
            data: data.map(i => i.value),
            barWidth: 8,
            itemStyle: {
              borderRadius: [0, 4, 4, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#0050cc' },
                { offset: 1, color: '#0099ff' }
              ])
            },
            label: { show: true, position: 'right', color: '#4dc9f6', fontSize: this.chartFontSmall }
          },
          {
            type: 'bar',
            data: data.map(i => i.value),
            barWidth: 8,
            barGap: '-100%',
            itemStyle: {
              borderRadius: [0, 4, 4, 0],
              color: 'rgba(0, 140, 255, 0.15)'
            },
            z: -1
          }
        ]
      }, false)
    },
    async fetchTopCities() {
      try {
        const res = await get('/admin/dashboard/topCities')
        this.topCities = res.data || []
      } catch (e) {
        console.error('获取地域用户数据失败:', e)
        this.topCities = []
      }
      // 更新图表数据，但不重置轮播位置
      this.updateRegionChart()
      // 仅在首次（轮播未启动时）初始化轮播
      if (!this.regionCycleTimer) {
        this.regionPage = 0
        this.startRegionCycle()
      }
    },
    startRegionCycle() {
      if (this.regionCycleTimer) clearInterval(this.regionCycleTimer)
      const allData = (this.topCities && this.topCities.length > 0)
        ? this.topCities.map(i => ({ name: i.city, value: i.userCount }))
        : []
      if (allData.length <= 8) return
      this.regionCycleTimer = setInterval(() => {
        this.regionPage++
        this.updateRegionChart()
      }, 1000)
    },
    initGaugeChart() {
      if (!this.$refs.gaugeChart) return
      this.gaugeChart = echarts.init(this.$refs.gaugeChart)
      this.gaugeChart.setOption({
        series: [{
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          radius: '90%',
          center: ['50%', '70%'],
          splitNumber: 5,
          axisLine: {
            lineStyle: {
              width: 8,
              color: [[0.5, '#00d68f'], [0.8, '#ffaa00'], [1, '#ff3366']]
            }
          },
          pointer: { show: false },
          axisTick: { show: false },
          splitLine: { length: 10, lineStyle: { color: 'auto', width: 2 } },
          axisLabel: { show: false },
          detail: {
            fontSize: 20,
            offsetCenter: [0, '-10%'],
            valueAnimation: true,
            formatter: '{value}',
            color: '#4dc9f6'
          },
          data: [{ value: 50 }]
        }]
      })
    },
    initPieChart() {
      if (!this.$refs.pieChart) return
      this.pieChart = echarts.init(this.$refs.pieChart)
      const data = this.adTypeRevenue
      const pieData = data.map(item => ({
        value: (item.ecpm || 0) / 1000,
        name: this.getAdTypeName(item.adType),
        itemStyle: { color: this.getEChartsPieColor(item.adType) }
      }))


      // 外圈刻度 - 均匀的3亮7暗
      const tickCount = 90
      const tickData = []
      for (let i = 0; i < tickCount; i++) {
        tickData.push({
          value: 1,
          itemStyle: {
            color: i % 9 < 3 ? 'rgba(0, 200, 255, 0.5)' : 'transparent',
            borderColor: 'transparent',
            borderWidth: 0
          }
        })
      }

      // 第二圈虚线刻度 - 相同均匀节奏
      const tick2Count = 72
      const tick2Data = []
      for (let i = 0; i < tick2Count; i++) {
        tick2Data.push({
          value: 1,
          itemStyle: {
            color: i % 9 < 2 ? 'rgba(0, 180, 255, 0.3)' : 'transparent',
            borderColor: 'transparent',
            borderWidth: 0
          }
        })
      }

      this.pieChart.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(6, 30, 65, 0.95)',
          borderColor: 'rgba(0, 160, 255, 0.5)',
          textStyle: { color: '#c8d8f0', fontSize: this.chartFontSmall },
          formatter: function (params) {
            return `<div style="font-weight:600;margin-bottom:4px;color:#fff;">${params.marker} ${params.name}</div>
              <div>收入：<b style="color:#00e5ff;">${params.value.toFixed(2)}元</b></div>
              <div>占比：<b style="color:#ffaa00;">${params.percent}%</b></div>`
          }
        },
        series: [
          // 1. 最外圈刻度
          {
            type: 'pie',
            radius: ['74%', '78%'],
            center: ['48%', '50%'],
            itemStyle: { borderColor: 'transparent', borderWidth: 0 },
            label: { show: false },
            emphasis: { scale: false },
            data: tickData,
            silent: true,
            z: 0
          },
          // 2. 薄外环
          {
            type: 'pie',
            radius: ['71%', '72.5%'],
            center: ['48%', '50%'],
            itemStyle: {
              color: 'rgba(0, 160, 255, 0.18)',
              borderColor: 'rgba(0, 100, 200, 0.25)',
              borderWidth: 1
            },
            label: { show: false },
            emphasis: { scale: false },
            data: [{ value: 1 }],
            silent: true,
            z: 1
          },
          // 3. 第二圈刻度（内侧虚线）
          {
            type: 'pie',
            radius: ['68%', '70%'],
            center: ['48%', '50%'],
            itemStyle: { borderColor: 'transparent', borderWidth: 0 },
            label: { show: false },
            emphasis: { scale: false },
            data: tick2Data,
            silent: true,
            z: 1
          },
          // 4. 外层漫射光晕（外扩）
          {
            type: 'pie',
            radius: ['58%', '78%'],
            center: ['48%', '50%'],
            itemStyle: {
              borderColor: 'transparent',
              borderWidth: 0,
              shadowBlur: 35,
              shadowColor: 'rgba(0, 180, 255, 0.4)'
            },
            label: { show: false },
            emphasis: { scale: false },
            data: pieData.map(d => ({
              ...d,
              itemStyle: { color: d.itemStyle.color, opacity: 0.08 }
            })),
            silent: true,
            z: 1
          },
          // 5. 内层光晕环（外扩）
          {
            type: 'pie',
            radius: ['52%', '58%'],
            center: ['48%', '50%'],
            itemStyle: {
              borderColor: 'transparent',
              borderWidth: 0,
              shadowBlur: 22,
              shadowColor: 'rgba(0, 210, 255, 0.45)'
            },
            label: { show: false },
            emphasis: { scale: false },
            data: pieData.map(d => ({
              ...d,
              itemStyle: { color: d.itemStyle.color, opacity: 0.25 }
            })),
            silent: true,
            z: 2
          },
          // 6. 主数据环
          {
            type: 'pie',
            radius: ['32%', '58%'],
            center: ['48%', '50%'],
            avoidLabelOverlap: false,
            label: {
              show: true,
              position: 'outside',
              formatter: function (params) {
                return `{name|${params.name}}\n{value|¥${params.value.toFixed(2)}}\n{percent|${params.percent}%}`
              },
              rich: {
                name: {
                  color: '#c8d8f0',
                  fontSize: 11,
                  lineHeight: 16,
                  align: 'center'
                },
                value: {
                  color: '#00e5ff',
                  fontSize: 12,
                  lineHeight: 18,
                  fontWeight: 'bold',
                  align: 'center'
                },
                percent: {
                  color: '#ffaa00',
                  fontSize: 10,
                  lineHeight: 14,
                  align: 'center'
                }
              }
            },
            labelLine: {
              show: true,
              length: 12,
              length2: 16,
              lineStyle: { color: 'rgba(0, 200, 255, 0.4)', width: 1.5 }
            },
            padAngle: 1.5,
            itemStyle: {
              borderRadius: 8,
              borderColor: 'transparent',
              borderWidth: 0,
              shadowBlur: 20,
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              shadowColor: 'rgba(0, 160, 255, 0.45)'
            },
            data: pieData,
            emphasis: {
              scaleSize: 10,
              itemStyle: {
                shadowBlur: 40,
                shadowColor: 'rgba(0, 230, 255, 0.75)',
                borderColor: 'rgba(0, 240, 255, 0.6)',
                borderWidth: 2
              }
            },
            z: 4
          },
          // 7. 内环装饰线1
          {
            type: 'pie',
            radius: ['26%', '29%'],
            center: ['48%', '50%'],
            itemStyle: {
              color: 'rgba(0, 180, 255, 0.15)',
              borderColor: 'rgba(0, 140, 230, 0.3)',
              borderWidth: 1
            },
            label: { show: false },
            data: [{ value: 1 }],
            emphasis: { scale: false },
            silent: true,
            z: 2
          },
          // 8. 内环装饰线2
          {
            type: 'pie',
            radius: ['22%', '24%'],
            center: ['48%', '50%'],
            itemStyle: {
              color: 'rgba(0, 140, 220, 0.12)',
              borderColor: 'rgba(0, 120, 200, 0.22)',
              borderWidth: 0.5,
              borderType: 'dashed'
            },
            label: { show: false },
            data: [{ value: 1 }],
            emphasis: { scale: false },
            silent: true,
            z: 2
          },
          // 9. 内部渐变填充
          {
            type: 'pie',
            radius: ['0%', '20%'],
            center: ['48%', '50%'],
            itemStyle: {
              color: new echarts.graphic.RadialGradient(0.5, 0.5, 0.7, [
                { offset: 0, color: 'rgba(3, 10, 35, 0.95)' },
                { offset: 0.6, color: 'rgba(2, 5, 22, 0.98)' },
                { offset: 1, color: 'rgba(0, 30, 65, 0.1)' }
              ])
            },
            label: { show: false },
            data: [{ value: 1 }],
            emphasis: { scale: false },
            silent: true,
            z: 1
          }
        ]
      })
    },
    initMonthChart() {
      if (!this.$refs.monthChart) return
      this.monthChart = echarts.init(this.$refs.monthChart)
      const data = this.monthlyNewUsers
      const hasData = data && data.length > 0
      const _x = hasData ? data.map(i => {
        const parts = i.month.split('-')
        return parseInt(parts[1], 10) + '月'
      }) : ['2月', '3月', '4月', '5月', '6月', '7月']
      const seriesData = hasData ? data.map(i => i.count) : [412, 634, 783, 1230, 876, 768]
      // 仅月份
      const xData = _x
      const maxVal = hasData ? Math.ceil(Math.max(...seriesData) * 1.2) || 10 : 1400
      this.monthChart.setOption({
        grid: { left: '2%', right: '2%', top: '8%', bottom: '18%', containLabel: true },
        xAxis: {
          type: 'category',
          data: xData,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#b0d0f0', fontSize: this.chartFontSmall + 5, lineHeight: 20 }
        },
        yAxis: {
          type: 'value',
          max: maxVal,
          maxInterval: 250,
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#6a8aae', fontSize: this.chartFontSmall, margin: 8 },
          splitLine: {
            show: true,
            lineStyle: { color: 'rgba(80, 130, 200, 0.15)', type: 'dashed', width: 1 }
          }
        },
        series: [{
          type: 'bar',
          data: seriesData,
          barWidth: '30%',
          barGap: '50%',
          label: {
            show: true,
            position: 'top',
            color: '#00e5ff',
            fontSize: this.chartFontSmall + 2,
            fontWeight: 'bold',
            formatter: function (params) {
              return seriesData[params.dataIndex]
            }
          },
          itemStyle: {
            borderRadius: [20, 20, 20, 20],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#0077cc' },
              { offset: 0.5, color: '#00aaff' },
              { offset: 1, color: '#33ddff' }
            ])
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#0088dd' },
                { offset: 0.5, color: '#00bbff' },
                { offset: 1, color: '#55eeff' }
              ]),
              shadowBlur: 10,
              shadowColor: 'rgba(0, 150, 255, 0.3)'
            }
          }
        },
        {
          type: 'bar',
          data: seriesData,
          barWidth: '30%',
          barGap: '-100%',
          itemStyle: {
            borderRadius: [20, 20, 20, 20],
            color: 'rgba(0, 50, 120, 0.2)'
          },
          silent: true,
          z: -1
        }]
      })
    },
    initWithdrawChart() {
      if (!this.$refs.withdrawChart) return
      this.withdrawChart = echarts.init(this.$refs.withdrawChart)
      this.updateWithdrawChart()
    },
    updateWithdrawChart() {
      if (!this.withdrawChart) return
      const { approvedCount, rejectedCount, approvalRate } = this.withdrawOverview
      const rate = approvalRate || 0
      this.withdrawChart.setOption({
        series: [{
          type: 'pie',
          radius: ['60%', '86%'],
          center: ['50%', '50%'],
          silent: true,
          label: { show: false },
          labelLine: { show: false },
          emphasis: { scale: false },
          data: [
            { value: approvedCount, itemStyle: { color: '#00d4ff' } },
            { value: rejectedCount, itemStyle: { color: 'rgba(26, 69, 128, 0.6)' } }
          ]
        }, {
          type: 'pie',
          radius: ['40%', '47%'],
          center: ['50%', '50%'],
          silent: true,
          label: { show: false },
          data: [{ value: 1, itemStyle: { color: 'rgba(0, 180, 255, 0.15)' } }]
        }],
        graphic: [{
          type: 'text',
          left: 'center',
          top: '35%',
          style: {
            text: rate.toFixed(1) + '%',
            textAlign: 'center',
            fill: '#00d4ff',
            fontSize: this.chartFontBase,
            fontWeight: 'bold'
          }
        },
        {
          type: 'text',
          left: 'center',
          top: '58%',
          style: {
            text: '通过率',
            textAlign: 'center',
            fill: '#8aa0c0',
            fontSize: this.chartFontSmall
          }
        }]
      })
    },
    async fetchWithdrawOverview() {
      try {
        const res = await get('/admin/dashboard/withdrawalOverview')
        this.withdrawOverview = res.data || { todayAmount: 0, totalAmount: 0, pendingCount: 0, approvedCount: 0, rejectedCount: 0, approvalRate: 0 }
        this.updateWithdrawChart()
      } catch (e) {
        console.error('获取提现概览失败:', e)
      }
    },
    handleResize() {
      if (this.regionChart) { this.regionChart.resize(); this.updateRegionChart() }
      if (this.gaugeChart) { this.gaugeChart.resize(); this.initGaugeChart() }
      if (this.pieChart) { this.pieChart.resize(); this.initPieChart() }
      if (this.monthChart) { this.monthChart.resize(); this.updateMonthChart() }
      if (this.withdrawChart) { this.withdrawChart.resize(); this.updateWithdrawChart() }
      const canvas = this.$refs.particleCanvas
      if (canvas) { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
    },
    disposeCharts() {
      if (this.regionChart) this.regionChart.dispose()
      if (this.gaugeChart) this.gaugeChart.dispose()
      if (this.pieChart) this.pieChart.dispose()
      if (this.monthChart) this.monthChart.dispose()
      if (this.withdrawChart) this.withdrawChart.dispose()
    }
  }
}
</script>

<style scoped>
.big-screen {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #041430 0%, #0a1d44 40%, #0d2b5e 70%, #122d5a 100%);
  color: #f0f4ff;
  overflow: hidden;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  position: relative;
}

/* 粒子画布 */
.particle-canvas {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

/* 动态光斑 - 若隐若现 */
.big-screen::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(ellipse 30% 40% at 20% 30%, rgba(0, 120, 255, 0.18) 0%, transparent 70%),
    radial-gradient(ellipse 25% 35% at 70% 20%, rgba(0, 200, 220, 0.14) 0%, transparent 70%),
    radial-gradient(ellipse 35% 45% at 50% 70%, rgba(100, 80, 255, 0.16) 0%, transparent 70%),
    radial-gradient(ellipse 20% 30% at 85% 65%, rgba(0, 150, 200, 0.12) 0%, transparent 70%),
    radial-gradient(ellipse 28% 38% at 10% 80%, rgba(50, 100, 255, 0.13) 0%, transparent 70%);
  animation: bgFloat 8s ease-in-out infinite;
}

@keyframes bgFloat {
  0%, 100% { opacity: 0.5; }
  25% { opacity: 0.9; }
  50% { opacity: 0.35; }
  75% { opacity: 0.85; }
}

/* 扫描线 - 从上往下 */
.big-screen::after {
  content: '';
  position: absolute;
  top: -10%;
  left: 0;
  width: 100%;
  height: 8%;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 180, 255, 0.06) 40%, rgba(0, 220, 255, 0.12) 50%, rgba(0, 180, 255, 0.06) 60%, transparent 100%);
  animation: scanLine 6s linear infinite;
}

@keyframes scanLine {
  0% { top: -10%; }
  100% { top: 110%; }
}

/* 顶部标题栏 */
.screen-header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 8vh;
  min-height: 48px;
  padding: 0 2vw;
  background: linear-gradient(180deg, rgba(8, 73, 173, 0.45) 0%, rgba(10, 35, 80, 0.95) 100%);
  border-bottom: 1px solid rgba(0, 160, 255, 0.4);
  box-shadow: 0 2px 24px rgba(0, 140, 255, 0.2);
  position: relative;
  z-index: 1;
}

.back-btn {
  position: absolute;
  left: 1.2vw;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 140, 255, 0.15);
  border: 1px solid rgba(0, 160, 255, 0.4);
  border-radius: 6px;
  color: #7dc8ff;
  font-size: max(12px, 0.8vw);
  padding: 4px 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  outline: none;
}

.back-btn:hover {
  background: rgba(0, 160, 255, 0.3);
  border-color: rgba(0, 200, 255, 0.6);
  color: #a4e8ff;
  box-shadow: 0 0 12px rgba(0, 160, 255, 0.25);
}

.back-btn svg {
  flex-shrink: 0;
}

/* 右上角实时时钟 */
.header-clock {
  position: absolute;
  right: 1.5vw;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.clock-time {
  font-size: max(18px, 1.3vw);
  font-weight: 700;
  font-family: 'Courier New', 'Consolas', monospace;
  color: #00e5ff;
  letter-spacing: 0.06em;
  text-shadow: 0 0 12px rgba(0, 200, 255, 0.4);
}

.clock-date {
  font-size: max(11px, 0.7vw);
  color: #8aa0c0;
  letter-spacing: 0.04em;
}

.screen-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 10%;
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 200, 255, 0.7), transparent);
}

.header-title {
  font-size: max(15px, 1.8vw);
  font-weight: 600;
  text-align: center;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.2em;
}

/* 主体布局 3列 x 2行 */
.screen-body {
  display: grid;
  grid-template-columns: 1.1fr 2fr 1fr;
  grid-template-rows: 1.2fr 1fr;
  gap: 1.2vh 1vw;
  padding: 1.2vh 1.2vw;
  height: 92vh;
  position: relative;
  z-index: 1;
}

/* 面板样式 */
.panel {
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

/* 面板光晕边框 */
.panel::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 24px;
  pointer-events: none;
  z-index: 2;
  box-shadow: inset 0 0 30px rgba(0, 160, 255, 0.12), 0 0 20px rgba(0, 130, 255, 0.06);
  animation: panelGlow 3s ease-in-out infinite;
}

@keyframes panelGlow {
  0%, 100% {
    box-shadow: inset 0 0 20px rgba(0, 160, 255, 0.08), 0 0 10px rgba(0, 130, 255, 0.04);
  }
  50% {
    box-shadow: inset 0 0 40px rgba(0, 200, 255, 0.2), 0 0 30px rgba(0, 160, 255, 0.12);
  }
}

.panel-title {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5vw;
  padding: max(1.6vh, 12px) 1vw max(1.2vh, 8px);
  min-height: max(48px, 5.5vh);
  background: linear-gradient(180deg, rgb(2, 0, 77) 0%, rgb(5, 10, 100) 50%, rgb(12, 25, 140) 100%);
  border: 2px solid rgb(13, 83, 183);
  border-bottom: none;
  border-radius: 24px 24px 0 0;
  font-size: max(16px, 1vw);
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 2.45;
  color: #00fff4;
}

.panel-title::before {
  content: '';
  position: absolute;
  top: max(6px, 0.8vh);
  left: 50%;
  transform: translateX(-50%);
  width: 50%;
  max-width: 300px;
  height: 5px;
  background: linear-gradient(90deg, transparent 0%, #0088ff 20%, #00e5ff 50%, #0088ff 80%, transparent 100%);
  border-radius: 1.5px;
  box-shadow: 0 0 8px rgba(0, 200, 255, 0.5);
}

.title-icon {
  width: 5px;
  height: max(16px, 1.1vw);
  background: linear-gradient(180deg, #00a6ff, #00fff4);
  border-radius: 2.5px;
  flex-shrink: 0;
}

.title-bar {
  width: max(24px, 1.8vw);
  height: 3px;
  background: linear-gradient(90deg, #0080ff, #00e5ff);
  border-radius: 2px;
  flex-shrink: 0;
}

.panel-content {
  flex: 1;
  padding: 1vh 1vw;
  overflow: auto;
  background: linear-gradient(180deg, rgb(2, 0, 77) 0%, rgb(5, 10, 100) 50%, rgb(12, 25, 140) 100%);
  border: 1px solid rgb(13, 83, 183);
  border-top: none;
  border-radius: 0 0 16px 16px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 200px;
}

/* 左侧 - 提现概览 2x2 网格 */
.withdraw-panel {
  display: flex;
  align-items: stretch;
  padding: max(4px, 0.5vh) max(4px, 0.4vw);
  min-height: 0;
}

.withdraw-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 3fr 2fr;
  gap: max(14px, 2vh) max(10px, 0.8vw);
  width: 100%;
  height: 100%;
}

.wd-card {
  background: linear-gradient(135deg, rgba(5, 38, 75, 0.85) 0%, rgba(4, 28, 55, 0.95) 100%);
  border: 1.5px solid rgba(0, 180, 255, 0.5);
  border-radius: 6px;
  padding: max(8px, 1vh) max(8px, 0.6vw);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  min-height: max(100px, 14vh);
  box-shadow: 0 0 8px rgba(0, 160, 255, 0.15), inset 0 0 12px rgba(0, 140, 255, 0.08);
}

.wd-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 30%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 200, 255, 0.4));
  border-radius: 0 0 0 2px;
}

.wd-label {
  font-size: max(11px, 0.72vw);
  color: #ffffff;
  text-align: left;
  width: 100%;
  margin-bottom: max(1px, 0.15vh);
  font-weight: 500;
  letter-spacing: 0.5px;
}

.wd-value {
  font-size: max(22px, 1.5vw);
  font-weight: 700;
  color: #f0f4ff;
  font-family: 'Arial', 'Helvetica Neue', sans-serif;
  line-height: 1.1;
  letter-spacing: 1px;
  text-shadow: 0 0 12px rgba(0, 200, 255, 0.25);
}

.wd-value-highlight {
  color: rgba(102, 255, 255, 1) !important;
  text-shadow: 0 0 14px rgba(102, 255, 255, 0.4);
}

.wd-value.wd-up {
  color: #00e676;
}

.wd-value.wd-down {
  color: #ff5252;
}

.wd-value.wd-pending {
  color: #ffaa00;
}

.wd-value-sm {
  font-size: max(18px, 1.3vw);
  font-weight: 700;
  color: #f0f4ff;
  font-family: 'Arial', sans-serif;
}

.wd-unit {
  font-size: max(10px, 0.65vw);
  color: #8aa0c0;
  font-weight: 400;
  margin-left: 2px;
}

.wd-sub {
  font-size: max(9px, 0.6vw);
  color: #5a7a9a;
  margin-top: max(1px, 0.15vh);
  letter-spacing: 0.3px;
}

/* 波浪线 */
.wd-wave {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: max(4px, 0.3vw);
  margin-top: auto;
  padding-top: max(6px, 0.8vh);
  height: max(28px, 3.5vh);
  width: 100%;
}

.wd-wave-bar {
  flex: 1;
  background: linear-gradient(180deg, #00e5ff 0%, #0080ff 60%, rgba(0, 80, 180, 0.4) 100%);
  border-radius: 3px 3px 0 0;
  min-height: 6px;
  animation: wavePulse 2.8s ease-in-out infinite alternate;
  box-shadow: 0 0 6px rgba(0, 200, 255, 0.3);
}

.wd-wave-bar:nth-child(1) { animation-delay: 0s; }
.wd-wave-bar:nth-child(2) { animation-delay: 0.25s; }
.wd-wave-bar:nth-child(3) { animation-delay: 0.5s; }
.wd-wave-bar:nth-child(4) { animation-delay: 0.75s; }
.wd-wave-bar:nth-child(5) { animation-delay: 1s; }
.wd-wave-bar:nth-child(6) { animation-delay: 1.25s; }
.wd-wave-bar:nth-child(7) { animation-delay: 1.5s; }
.wd-wave-bar:nth-child(8) { animation-delay: 1.75s; }
.wd-wave-bar:nth-child(9) { animation-delay: 2s; }
.wd-wave-bar:nth-child(10) { animation-delay: 2.25s; }

@keyframes wavePulse {
  0% { opacity: 0.5; transform: scaleY(0.7); }
  50% { opacity: 1; transform: scaleY(1); }
  100% { opacity: 0.5; transform: scaleY(0.7); }
}

/* 右上 - 图表卡片 */
.wd-card-chart {
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.wd-card-chart .wd-label {
  text-align: left;
}

.wd-donut-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: max(8px, 0.8vw);
  flex: 1;
  min-height: 0;
}

.wd-donut {
  width: max(56px, 5vw);
  height: max(56px, 5vw);
  flex-shrink: 0;
}

.wd-donut-legend {
  display: flex;
  flex-direction: column;
  gap: max(3px, 0.35vh);
  font-size: max(10px, 0.65vw);
  color: #8aa0c0;
}

.wd-donut-legend span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.wd-donut-legend i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* 中间上方面板内布局 */
.panel-center-content {
  display: flex;
  flex-direction: column;
  gap: max(10px, 1.2vh);
}

/* 今日广告收入数字翻牌器 */
.center-top {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0;
}

.online-section {
  text-align: center;
}

.online-label {
  font-size: max(17px, 1.3vw);
  color: #8cc8ff;
  margin-bottom: max(6px, 0.6vh);
  font-weight: 600;
}

.online-numbers {
  display: flex;
  justify-content: center;
  gap: max(5px, 0.45vw);
}

.number-circle {
  width: max(38px, 3vw);
  height: max(54px, 5.6vh);
  background: linear-gradient(180deg, #0d47a1 0%, #051a40 100%);
  border: 2px solid #00a8ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: max(26px, 2.1vw);
  font-weight: 700;
  color: #00e5ff;
  font-family: 'Arial', sans-serif;
  box-shadow: 0 0 14px rgba(0, 200, 255, 0.32);
  animation: digitFlipIn 0.35s ease-out;
}

@keyframes digitFlipIn {
  0% {
    opacity: 0;
    transform: perspective(400px) rotateX(-90deg);
    box-shadow: 0 0 0 rgba(0, 200, 255, 0);
  }
  60% {
    opacity: 1;
    transform: perspective(400px) rotateX(10deg);
  }
  80% {
    transform: perspective(400px) rotateX(-4deg);
  }
  100% {
    opacity: 1;
    transform: perspective(400px) rotateX(0deg);
    box-shadow: 0 0 14px rgba(0, 200, 255, 0.32);
  }
}

.progress-rings {
  display: flex;
  gap: max(8px, 0.5vw);
}

.ring-side {
  flex: 0 0 auto;
}

.ring-item {
  background: rgba(5, 28, 65, 0.6);
  border-radius: 50%;
  padding: 3px;
}

/* 统一数据网格：5列 x 2行 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: max(7px, 0.6vw);
  flex: 2;
}

/* ===== 统计卡片 - 科技感升级 ===== */
.stat-card {
  position: relative;
  background: linear-gradient(135deg, rgba(5, 38, 75, 0.85) 0%, rgba(4, 28, 55, 0.95) 100%);
  border: 1px solid rgba(0, 140, 255, 0.28);
  border-radius: 8px;
  padding: max(6px, 0.7vh) max(8px, 0.6vw);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: max(50px, 5.8vh);
  transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1.2);
  cursor: default;
  overflow: hidden;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(0, 180, 255, 0.06);
}

/* 右上角 + 左下角 科技边角装饰 */
.stat-card::before {
  content: '';
  position: absolute;
  top: 3px;
  right: 3px;
  width: 10px;
  height: 10px;
  border-top: 1.5px solid rgba(0, 190, 255, 0.2);
  border-right: 1.5px solid rgba(0, 190, 255, 0.2);
  transition: all 0.35s ease;
}

.stat-card::after {
  content: '';
  position: absolute;
  bottom: 3px;
  left: 3px;
  width: 10px;
  height: 10px;
  border-bottom: 1.5px solid rgba(0, 190, 255, 0.2);
  border-left: 1.5px solid rgba(0, 190, 255, 0.2);
  transition: all 0.35s ease;
}

/* 悬浮效果 */
.stat-card:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 210, 255, 0.55);
  box-shadow:
    0 8px 28px rgba(0, 130, 255, 0.18),
    0 0 18px rgba(0, 180, 255, 0.12),
    inset 0 1px 0 rgba(0, 200, 255, 0.12);
  background: linear-gradient(135deg, rgba(8, 48, 90, 0.88) 0%, rgba(5, 34, 68, 0.97) 100%);
}

.stat-card:hover::before,
.stat-card:hover::after {
  border-color: rgba(0, 220, 255, 0.6);
  width: 14px;
  height: 14px;
}

/* ===== 高亮卡片 - 脉冲光晕 ===== */
.stat-card.highlight {
  background: linear-gradient(135deg, rgba(0, 130, 230, 0.3) 0%, rgba(0, 155, 245, 0.2) 100%);
  border-color: rgba(0, 180, 255, 0.55);
  box-shadow:
    0 0 22px rgba(0, 160, 255, 0.22),
    0 0 10px rgba(0, 200, 255, 0.1),
    inset 0 0 22px rgba(0, 140, 255, 0.06);
}

/* 高亮卡片-背景脉冲光晕 */
.stat-card.highlight::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  border: none;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 200, 255, 0.08) 0%,
    transparent 70%
  );
  animation: cardPulse 3s ease-in-out infinite;
  pointer-events: none;
  border-radius: 50%;
}

.stat-card.highlight::after {
  bottom: 3px;
  left: 3px;
  border-bottom: 1.5px solid rgba(0, 220, 255, 0.55);
  border-left: 1.5px solid rgba(0, 220, 255, 0.55);
}

.stat-card.highlight:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 230, 255, 0.7);
  box-shadow:
    0 10px 36px rgba(0, 160, 255, 0.3),
    0 0 24px rgba(0, 200, 255, 0.2),
    inset 0 0 30px rgba(0, 160, 255, 0.1);
  background: linear-gradient(135deg, rgba(0, 150, 245, 0.38) 0%, rgba(0, 170, 255, 0.25) 100%);
}

/* ===== 数值样式 ===== */
.stat-value {
  font-size: max(18px, 1.4vw);
  font-weight: 700;
  color: rgba(102, 255, 255, 1);
  font-family: 'Arial', sans-serif;
  line-height: 1.1;
  margin-bottom: max(3px, 0.3vh);
  position: relative;
  z-index: 1;
  transition: color 0.3s ease;
}

.stat-card.highlight .stat-value {
  font-size: max(22px, 1.7vw);
  color: rgba(102, 255, 255, 1);
  text-shadow: 0 0 14px rgba(102, 255, 255, 0.5);
}

.stat-card:hover .stat-value {
  color: rgba(153, 255, 255, 1);
}

.stat-card.highlight:hover .stat-value {
  color: rgba(179, 255, 255, 1);
  text-shadow: 0 0 18px rgba(102, 255, 255, 0.65);
}

/* ===== 标签 + 指示圆点 ===== */
.stat-label {
  font-size: max(9px, 0.62vw);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: max(4px, 0.3vw);
  position: relative;
  z-index: 1;
  transition: color 0.3s ease;
}

.stat-card:hover .stat-label {
  color: #ffffff;
}

.stat-dot {
  width: max(5px, 0.35vw);
  height: max(5px, 0.35vw);
  border-radius: 50%;
  background: rgba(0, 200, 255, 0.5);
  flex-shrink: 0;
  box-shadow: 0 0 6px rgba(0, 200, 255, 0.35);
  transition: all 0.3s ease;
}

.stat-card.highlight .stat-dot {
  background: rgba(0, 229, 255, 0.7);
  box-shadow: 0 0 8px rgba(0, 229, 255, 0.5);
}

.stat-card:hover .stat-dot {
  background: rgba(0, 230, 255, 0.75);
  box-shadow: 0 0 10px rgba(0, 230, 255, 0.6);
}

/* ===== 脉冲动画 ===== */
@keyframes cardPulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 0.85;
  }
}



.ad-overview {
  display: flex;
  align-items: center;
  gap: 0.3vw;
  padding: 0 0 0 max(4px, 0.3vw);
  overflow: visible;
}

.ad-overview-empty {
  padding-left: 0;
}

.ad-chart-area {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: visible;
}

.pie-chart {
  width: min(400px, 26vw);
  height: min(400px, 34vh);
  overflow: visible;
}

/* 图例区域 - 统一右侧，压缩宽度 */
.ad-legend {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: max(3px, 0.3vh);
  min-width: max(140px, 10vw);
  max-width: max(200px, 15vw);
  margin-right: 2vw;
}

.legend-item {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 8px;
  padding: max(4px, 0.4vh) max(30px, 1.8vw);
  border-radius: 3px;
  font-size: max(10px, 0.7vw);
  transition: background 0.2s;
}

.legend-item:hover {
  background: rgba(0, 140, 220, 0.18);
}

.legend-item .dot {
  width: max(8px, 0.6vw);
  height: max(8px, 0.6vw);
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-item .name {
  color: #c8d8f0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 广告为空时的提示 */
.ad-empty-hint {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ad-empty-text {
  font-size: max(14px, 0.9vw);
  color: #ffffff;
  letter-spacing: 0.08em;
}

/* 右侧 - 用户累计余额排行榜 */
.earned-list {
  display: flex;
  flex-direction: column;
  gap: max(4px, 0.5vh);
}

.earned-item {
  display: grid;
  grid-template-columns: max(18px, 1.2vw) max(22px, 1.5vw) 6em 1fr max(35px, 4vw);
  align-items: center;
  gap: max(3px, 0.3vw);
  padding: max(2px, 0.2vh) 0;
}

.earned-rank {
  width: max(18px, 1.2vw);
  height: max(18px, 1.2vw);
  background: rgba(100, 140, 180, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: max(10px, 0.6vw);
  font-weight: 600;
  color: #a0c0e0;
  flex-shrink: 0;
  justify-self: center;
}

.earned-rank.top3 {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  color: #fff;
}

.earned-avatar {
  width: max(22px, 1.5vw);
  height: max(22px, 1.5vw);
  border-radius: 50%;
  border: 1px solid rgba(0, 160, 255, 0.4);
  object-fit: cover;
  flex-shrink: 0;
  justify-self: center;
}

.earned-name {
  font-size: max(11px, 0.7vw);
  color: #c8d8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 6em;
  flex-shrink: 0;
}

.earned-bar {
  height: max(4px, 0.4vh);
  background: rgba(0, 100, 180, 0.3);
  border-radius: 3px;
  overflow: hidden;
  align-self: center;
}

.earned-progress {
  height: 100%;
  background: linear-gradient(90deg, #0078ff, #00d4ff);
  border-radius: 3px;
  transition: width 0.5s ease;
}

.earned-value {
  font-size: max(11px, 0.7vw);
  color: #4dc9f6;
  font-weight: 600;
  text-align: right;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: rgba(5, 30, 65, 0.5);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 160, 255, 0.4);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 160, 255, 0.6);
}
</style>
